import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { useTranslation } from "react-i18next";
import type { RevenueKpiSet } from "@/hooks/useRevenueReport";

interface RevenueKPIsProps {
  current: RevenueKpiSet;
  previous: RevenueKpiSet;
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

function calcDelta(cur: number, prev: number) {
  if (prev === 0) return null;
  return ((cur - prev) / prev) * 100;
}


function DeltaBadge({ pct }: { pct: number | null }) {
  if (pct === null) return null;
  const positive = pct > 0;
  const negative = pct < 0;
  return (
    <span
      className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-bold ${
        positive
          ? "bg-green-100 text-green-800"
          : negative
          ? "bg-red-100 text-red-800"
          : "bg-muted text-muted-foreground"
      }`}
    >
      {positive ? (
        <TrendingUp className="h-3 w-3" />
      ) : negative ? (
        <TrendingDown className="h-3 w-3" />
      ) : (
        <Minus className="h-3 w-3" />
      )}
      {pct > 0 ? "+" : ""}
      {pct.toFixed(1)}%
    </span>
  );
}

interface KpiCardProps {
  label: string;
  value: number;
  prev: number;
}

function KpiCard({ label, value, prev }: KpiCardProps) {
  const d = calcDelta(value, prev);
  return (
    <div className="rounded-2xl border bg-card p-5 flex flex-col gap-1">
      <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
        {label}
      </span>
      <div className="flex items-baseline gap-2 flex-wrap mt-1">
        <span className="text-3xl font-extrabold tracking-tight">{formatCurrency(value)}</span>
        <DeltaBadge pct={d} />
      </div>
      {prev > 0 && (
        <p className="text-xs text-muted-foreground mt-0.5">
          {formatCurrency(prev)}
        </p>
      )}
    </div>
  );
}

export function RevenueKPIs({ current, previous }: RevenueKPIsProps) {
  const { t } = useTranslation("reports");
  return (
    <div className="grid gap-4 grid-cols-1 sm:grid-cols-2 lg:grid-cols-4">
      <KpiCard
        label={t("kpiGrossRevenue")}
        value={current.grossRevenue}
        prev={previous.grossRevenue}
      />
      <KpiCard
        label={t("kpiNetRevenue")}
        value={current.netRevenue}
        prev={previous.netRevenue}
      />
      <KpiCard
        label={t("kpiServiceRevenue")}
        value={current.serviceRevenue}
        prev={previous.serviceRevenue}
      />
      <KpiCard
        label={t("kpiProductRevenue")}
        value={current.productRevenue}
        prev={previous.productRevenue}
      />
    </div>
  );
}
